import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  BarChart, 
  Bar, 
  PieChart, 
  Pie, 
  Cell, 
  ComposedChart 
} from 'recharts';
import { useApi } from '../../api/api';
import './TripEconomistDetails.css';
import ChokeAnalysis from '../ChokeAnalysis/ChokeAnalysis';
import AclScenarioComponent from '../AclScenario/AclScenario';
import Modal from '../common/Modal/Modal';


// Collapsible Section Component
const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="collapsible-section">
      <button 
        className="collapsible-header"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <span className="collapsible-title">{title}</span>
        <span className={`collapsible-icon ${isOpen ? 'open' : ''}`}>▼</span>
      </button>
      {isOpen && (
        <div className="collapsible-content">
          {children}
        </div>
      )}
    </div>
  );
};

// Expense Breakdown Component
const ExpenseBreakdownComponent = ({ expenseAnalysis, formatCurrency, EXPENSE_COLORS }) => {
  return (
    <div className="insight-container half-width">
      <h3>Expense Breakdown</h3>
      {expenseAnalysis.length > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
          <div style={{ flex: 1 }}>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={expenseAnalysis}
                  cx="50%"
                  cy="50%"
                  innerRadius={30}
                  outerRadius={70}
                  paddingAngle={5}
                  dataKey="percentage"
                >
                  {expenseAnalysis.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`${value}%`]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div style={{ flex: 1 }}>
            <div className="table-container">
              <table className="results-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Amount</th>
                    <th>%</th>
                  </tr>
                </thead>
                <tbody>
                  {expenseAnalysis.map((item, index) => (
                    <tr key={item.name}>
                      <td>
                        <span style={{ 
                          display: 'inline-block', 
                          width: '12px', 
                          height: '12px', 
                          backgroundColor: EXPENSE_COLORS[index],
                          marginRight: '8px',
                          borderRadius: '2px'
                        }}></span>
                        {item.name}
                      </td>
                      <td>{formatCurrency(item.value)}</td>
                      <td>{item.percentage}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No expense data available
        </div>
      )}
    </div>
  );
};

// Port Comparison Component - FIXED VERSION
const PortComparisonComponent = ({ portComparison, formatCurrency }) => {
  return (
    <div className="insight-container half-width">
      <h3>Port Comparison - Revenue</h3>
      {portComparison && portComparison.length > 0 ? (
        <ResponsiveContainer width="100%" height={320}>
          <BarChart 
            data={portComparison}
            margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="port" 
              angle={-45} 
              textAnchor="end" 
              height={80}
              interval={0}
              fontSize={11}
              tick={{ fontSize: 11 }}
            />
            <YAxis 
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`}
              fontSize={11}
            />
            <Tooltip 
              formatter={(value) => [formatCurrency(value), 'Revenue']}
              labelStyle={{ fontSize: '12px' }}
              contentStyle={{ fontSize: '12px' }}
            />
            <Bar dataKey="value" fill="#3b82f6" />
          </BarChart>
        </ResponsiveContainer>
      ) : (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No port comparison data available
        </div>
      )}
    </div>
  );
};

// Single Fleet Quota Utilization Component (for non-ACL scenarios)
const SingleFleetQuotaUtilizationComponent = ({ 
  fleetQuotaUtilization, 
  getQuotaUtilizationColor 
}) => {
  return (
    <div className="insight-container full-width">
      <h3>Fleet Quota Utilization by Species</h3>
      {fleetQuotaUtilization && fleetQuotaUtilization.success ? (
        <div style={{ height: '400px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart 
              data={fleetQuotaUtilization.species_breakdown}
              margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="species_name" 
                angle={-45}
                textAnchor="end"
                height={100}
                interval={0}
                fontSize={11}
              />
              <YAxis label={{ value: 'Utilization %', angle: -90, position: 'insideLeft' }} />
              <Tooltip 
                formatter={(value, name) => {
                  if (name === 'utilization_pct') {
                    return [`${value.toFixed(1)}%`, 'Utilization'];
                  }
                  return [value, name];
                }}
              />
              <Bar 
                dataKey="utilization_pct" 
                fill={(entry) => getQuotaUtilizationColor(entry.utilization_pct)}
                name="Utilization %"
              >
                {fleetQuotaUtilization.species_breakdown.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={getQuotaUtilizationColor(entry.utilization_pct)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No fleet quota utilization data available
        </div>
      )}
    </div>
  );
};

// Vessel Quota Utilization Component
const VesselQuotaUtilizationComponent = ({ quotaAnalysis, selectedVessel, formatCurrency }) => {
  return (
    <div className="insight-container full-width">
      <h3>Vessel Quota Utilization - {selectedVessel}</h3>
      {quotaAnalysis.analysis.length > 0 ? (
        <>
          <div style={{ height: '400px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={quotaAnalysis.analysis}
                margin={{ top: 20, right: 30, left: 40, bottom: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  interval={0}
                  fontSize={11}
                />
                <YAxis label={{ value: 'Utilization %', angle: -90, position: 'insideLeft' }} />
                <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Utilization']} />
                <Bar dataKey="utilization" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {quotaAnalysis.chokeSpecies && (
            <div className="metric-card" style={{marginTop: '16px', backgroundColor: '#fef3c7'}}>
              <h4 style={{color: '#92400e'}}>Choke Species: {quotaAnalysis.chokeSpecies.name}</h4>
              <p style={{color: '#92400e', margin: '8px 0'}}>
                Utilization: {quotaAnalysis.chokeSpecies.utilization}%
              </p>
              <p style={{color: '#92400e', margin: '0'}}>
                Total Missed Revenue: {formatCurrency(quotaAnalysis.analysis.reduce((sum, species) => sum + species.missedRevenue, 0))}
              </p>
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No quota utilization data available - check vessel quota configuration
        </div>
      )}
    </div>
  );
};

// MAIN COMPONENT: Trip Economist Details with isolated data management
const TripEconomistDetails = () => {
  // Core selection state
  const [selectedScope, setSelectedScope] = useState('fleet');
  const [selectedVessel, setSelectedVessel] = useState('');
  const [selectedPort, setSelectedPort] = useState('');
  const [selectedYear, setSelectedYear] = useState('1');
  
  // Session management state
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [sessionLoading, setSessionLoading] = useState(true);
  
  // ACL Sensitivity Analysis state
  const [aclAnalyses, setAclAnalyses] = useState([]);
  const [aclDataLoading, ] = useState(false);
  
  // Data state
  const [allTripResults, setAllTripResults] = useState([]); // Raw data from API
  const [vessels, setVessels] = useState([]);
  const [ports, setPorts] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Asset Valuation and Quota Trading Data
  const [allAssetValuations, setAllAssetValuations] = useState([]);
  const [allQuotaTransactions, setAllQuotaTransactions] = useState([]);
  const [quotaTradingActivity, setQuotaTradingActivity] = useState([]);
  const [fleetQuotaUtilization, setFleetQuotaUtilization] = useState(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);

  const apiRef = useRef(useApi());
  const { 
    getTripResults, 
    getFishingVessels, 
    getFishingPorts,
    getAssetValuations,
    getQuotaMarketTransactions,
    getQuotaTradingActivity,
    getFleetQuotaUtilization,
    getSessions,
    getAclSensitivityAnalyses
  } = apiRef.current;

  // Colors for charts
  const EXPENSE_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];

  // Load ACL Sensitivity Analyses
  const loadAclAnalyses = useCallback(async () => {
    try {
      console.log('Loading ACL sensitivity analyses...');
      const response = await getAclSensitivityAnalyses();
      
      let analysesData = [];
      if (response?.data?.success && response?.data?.results) {
        analysesData = response.data.results;
      } else if (Array.isArray(response?.data)) {
        analysesData = response.data;
      }
      
      console.log('ACL analyses loaded:', analysesData.length);
      setAclAnalyses(analysesData);
      
    } catch (err) {
      console.error('Error loading ACL analyses:', err);
      // Don't set error state for ACL analyses as they're optional
    }
  }, [getAclSensitivityAnalyses]);

  // Load sessions on component mount
  const loadSessions = useCallback(async () => {
    try {
      setSessionLoading(true);
      console.log('Loading sessions...');
      
      const response = await getSessions();
      let sessionData = [];
      
      if (response?.data?.success && response?.data?.results) {
        sessionData = response.data.results;
      } else if (Array.isArray(response?.data)) {
        sessionData = response.data;
      }
      
      console.log('Sessions loaded:', sessionData);
      setSessions(sessionData);
      
      // Auto-select the first session if available
      if (sessionData.length > 0 && !selectedSessionId) {
        const firstSession = sessionData[0];
        const sessionId = firstSession.session_id;
        console.log('Auto-selecting first session:', sessionId);
        setSelectedSessionId(sessionId);
      }
      
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError(`Failed to load sessions: ${err.message}`);
    } finally {
      setSessionLoading(false);
    }
  }, [getSessions, selectedSessionId]);

  // Load sessions and ACL analyses when component mounts
  useEffect(() => {
    loadSessions();
    loadAclAnalyses();
  }, [loadSessions, loadAclAnalyses]);

  // PURE DATA FILTERING (no side effects)
  const filteredTripData = useMemo(() => {
    let filtered = [...allTripResults];

    // Apply scope filter
    if (selectedScope === 'vessel' && selectedVessel) {
      filtered = filtered.filter(trip => trip.vessel_name === selectedVessel);
    } else if (selectedScope === 'port' && selectedPort) {
      filtered = filtered.filter(trip => trip.port_name === selectedPort);
    }

    // Apply year filter
    if (selectedYear !== 'total') {
      filtered = filtered.filter(trip => 
        trip.simulation_year === parseInt(selectedYear) || 
        trip.year === parseInt(selectedYear)
      );
    }

    return filtered;
  }, [allTripResults, selectedScope, selectedVessel, selectedPort, selectedYear]);

  // Asset Valuation Filtering
  const filteredAssetValuations = useMemo(() => {
    let filtered = [...allAssetValuations];

    // Apply scope filter
    if (selectedScope === 'vessel' && selectedVessel) {
      filtered = filtered.filter(valuation => valuation.vessel_name === selectedVessel);
    } else if (selectedScope === 'port' && selectedPort) {
      // Get vessels that operate from this port
      const portVessels = vessels
        .filter(v => v.home_port === selectedPort)
        .map(v => v.name);
      filtered = filtered.filter(valuation => portVessels.includes(valuation.vessel_name));
    }

    // Apply year filter
    if (selectedYear !== 'total') {
      filtered = filtered.filter(valuation => 
        valuation.simulation_year === parseInt(selectedYear)
      );
    }

    return filtered;
  }, [allAssetValuations, selectedScope, selectedVessel, selectedPort, selectedYear, vessels]);

  // Quota Transactions Filtering
  const filteredQuotaTransactions = useMemo(() => {
    let filtered = [...allQuotaTransactions];

    // Apply scope filter
    if (selectedScope === 'vessel' && selectedVessel) {
      filtered = filtered.filter(transaction => 
        transaction.buyer_vessel === selectedVessel || 
        transaction.seller_vessel === selectedVessel
      );
    } else if (selectedScope === 'port' && selectedPort) {
      // Get vessels that operate from this port
      const portVessels = vessels
        .filter(v => v.home_port === selectedPort)
        .map(v => v.name);
      filtered = filtered.filter(transaction => 
        portVessels.includes(transaction.buyer_vessel) || 
        portVessels.includes(transaction.seller_vessel)
      );
    }

    // Apply year filter
    if (selectedYear !== 'total') {
      filtered = filtered.filter(transaction => 
        transaction.simulation_year === parseInt(selectedYear)
      );
    }

    return filtered;
  }, [allQuotaTransactions, selectedScope, selectedVessel, selectedPort, selectedYear, vessels]);

  // Top 5 Vessels Analysis Function
  const getTop5VesselsByRevenue = useCallback((data) => {
    if (!data.length) return [];

    // Group trips by vessel and calculate total revenue across ALL trips
    const vesselStats = {};
    data.forEach(trip => {
      const vesselName = trip.vessel_name;
      if (!vesselStats[vesselName]) {
        vesselStats[vesselName] = {
          vesselName,
          totalRevenue: 0,
          totalTrips: 0,
          totalCosts: 0,
          totalProfit: 0,
          totalLandings: 0
        };
      }
      
      vesselStats[vesselName].totalRevenue += trip.total_revenue || 0;
      vesselStats[vesselName].totalCosts += trip.total_costs || 0;
      vesselStats[vesselName].totalProfit += trip.final_net_profit || 0;
      vesselStats[vesselName].totalTrips += 1;
      vesselStats[vesselName].totalLandings += trip.total_landings_lbs || 0;
    });

    // Calculate derived metrics and sort by total revenue
    const vesselArray = Object.values(vesselStats).map(vessel => ({
      ...vessel,
      avgRevenuePerTrip: vessel.totalTrips > 0 ? vessel.totalRevenue / vessel.totalTrips : 0,
      profitMargin: vessel.totalRevenue > 0 ? (vessel.totalProfit / vessel.totalRevenue * 100) : 0,
      revenuePerPound: vessel.totalLandings > 0 ? vessel.totalRevenue / vessel.totalLandings : 0
    }));

    return vesselArray
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 5);
  }, []);

  // Top 5 Vessels Analysis - MUST come before mapData
  const top5VesselsAnalysis = useMemo(() => 
    selectedScope === 'top5' ? getTop5VesselsByRevenue(filteredTripData) : null, 
    [selectedScope, getTop5VesselsByRevenue, filteredTripData]
  );

  // Filter Asset Valuations for Top 5
  const finalFilteredAssetValuations = useMemo(() => {
    if (selectedScope === 'top5' && top5VesselsAnalysis && top5VesselsAnalysis.length > 0) {
      const top5VesselNames = top5VesselsAnalysis.map(vessel => vessel.vesselName);
      return filteredAssetValuations.filter(valuation => 
        top5VesselNames.includes(valuation.vessel_name)
      );
    }
    return filteredAssetValuations;
  }, [selectedScope, top5VesselsAnalysis, filteredAssetValuations]);

  // Filter Quota Transactions for Top 5
  const finalFilteredQuotaTransactions = useMemo(() => {
    if (selectedScope === 'top5' && top5VesselsAnalysis && top5VesselsAnalysis.length > 0) {
      const top5VesselNames = top5VesselsAnalysis.map(vessel => vessel.vesselName);
      return filteredQuotaTransactions.filter(transaction => 
        top5VesselNames.includes(transaction.buyer_vessel) || 
        top5VesselNames.includes(transaction.seller_vessel)
      );
    }
    return filteredQuotaTransactions;
  }, [selectedScope, top5VesselsAnalysis, filteredQuotaTransactions]);

  // SCOPE CHANGE HANDLER (only updates state)
  const handleScopeChange = useCallback((newScope) => {
    console.log('Scope change:', { from: selectedScope, to: newScope });
    setSelectedScope(newScope);
    setSelectedVessel('');
    setSelectedPort('');
    
    // Set default year to "1" for fleet scope
    if (newScope === 'fleet') {
      setSelectedYear('1');
    }
  }, [selectedScope]);

  // Fleet Quota Utilization Analysis
  const getQuotaUtilizationColor = useCallback((utilization) => {
    if (utilization >= 70 && utilization <= 95) return '#10b981'; // Green - good utilization
    if (utilization < 70) return '#f59e0b'; // Yellow - underutilized
    return '#ef4444'; // Red - over-utilized
  }, []);

  // DATA LOADING (independent of map)
  const loadData = useCallback(async () => {
    console.log('=== LOADING TRIP ECONOMIST DATA ===');
    
    // Don't load data if no session is selected
    if (!selectedSessionId) {
      console.log('No session selected, skipping data load');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);

      // Build filters based on current selection - ALWAYS include session_id
      const filters = {
        session_id: selectedSessionId  // REQUIRED: Include session ID in all requests
      };
      
      if (selectedScope === 'vessel' && selectedVessel) {
        filters.vessel_name = selectedVessel;
      } else if (selectedScope === 'port' && selectedPort) {
        filters.port_name = selectedPort;
      } else if (selectedScope === 'fleet' || selectedScope === 'top5') {
        // For fleet and top5, load more data to get comprehensive view
        filters.limit = 500;
      }

      // Build asset valuation filters
      const assetFilters = {
        session_id: selectedSessionId  // REQUIRED: Include session ID
      };
      if (selectedScope === 'vessel' && selectedVessel) {
        assetFilters.vessel_name = selectedVessel;
      }
      if (selectedYear !== 'total') {
        assetFilters.simulation_year = parseInt(selectedYear);
      }

      // Build quota transaction filters
      const quotaFilters = {
        session_id: selectedSessionId  // REQUIRED: Include session ID
      };
      if (selectedScope === 'vessel' && selectedVessel) {
        quotaFilters.vessel_name = selectedVessel;
      }
      if (selectedYear !== 'total') {
        quotaFilters.simulation_year = parseInt(selectedYear);
      }

      // Build trading activity filters (only for fleet scope)
      const activityFilters = {
        session_id: selectedSessionId  // REQUIRED: Include session ID
      };
      if (selectedYear !== 'total') {
        activityFilters.simulation_year = parseInt(selectedYear);
      }

      const promises = [
        getTripResults(filters),
        getFishingVessels(),
        getFishingPorts(),
        getAssetValuations(assetFilters),
        getQuotaMarketTransactions(quotaFilters)
      ];

      // Only load trading activity and fleet quota utilization for fleet scope
      if (selectedScope === 'fleet') {
        promises.push(getQuotaTradingActivity(activityFilters));
        promises.push(getFleetQuotaUtilization({
          session_id: selectedSessionId,
          simulation_year: selectedYear !== 'total' ? parseInt(selectedYear) : null
        }));
      }

      const responses = await Promise.all(promises);
      const [tripResponse, vesselResponse, portResponse, assetResponse, quotaResponse, activityResponse, fleetQuotaResponse] = responses;

      // Extract trip data
      let trips = [];
      if (tripResponse?.data?.success && tripResponse?.data?.results) {
        trips = tripResponse.data.results;
      } else if (Array.isArray(tripResponse?.data)) {
        trips = tripResponse.data;
      }

      // Extract vessel data
      let vesselsData = [];
      if (vesselResponse?.data?.success && vesselResponse?.data?.results) {
        vesselsData = vesselResponse.data.results;
      } else if (Array.isArray(vesselResponse?.data)) {
        vesselsData = vesselResponse.data;
      }

      // Extract port data
      let portsData = [];
      if (portResponse?.data?.success && portResponse?.data?.results) {
        portsData = portResponse.data.results;
      } else if (Array.isArray(portResponse?.data)) {
        portsData = portResponse.data;
      }

      // Extract asset valuation data
      let assetData = [];
      if (assetResponse?.data?.success && assetResponse?.data?.results) {
        assetData = assetResponse.data.results;
      } else if (Array.isArray(assetResponse?.data)) {
        assetData = assetResponse.data;
      }

      // Extract quota transaction data
      let quotaData = [];
      if (quotaResponse?.data?.success && quotaResponse?.data?.results) {
        quotaData = quotaResponse.data.results;
      } else if (Array.isArray(quotaResponse?.data)) {
        quotaData = quotaResponse.data;
      }

      // Extract trading activity data (only if fleet scope)
      let activityData = [];
      if (selectedScope === 'fleet' && activityResponse) {
        if (activityResponse?.data?.success && activityResponse?.data?.results) {
          activityData = activityResponse.data.results;
        } else if (Array.isArray(activityResponse?.data)) {
          activityData = activityResponse.data;
        }
      }

      // Extract fleet quota utilization data (only if fleet scope)
      let fleetQuotaData = null;
      if (selectedScope === 'fleet' && fleetQuotaResponse) {
        if (fleetQuotaResponse?.data?.success) {
          fleetQuotaData = fleetQuotaResponse.data;
        } else if (fleetQuotaResponse?.data) {
          fleetQuotaData = fleetQuotaResponse.data;
        }
      }

      // Filter vessels and ports to only show those with trip data
      const vesselsWithTrips = vesselsData.filter(vessel => 
        trips.some(trip => trip.vessel_name === vessel.name)
      );
      const portsWithTrips = portsData.filter(port => {
        const portName = port.name || port;
        return trips.some(trip => trip.port_name === portName);
      });

      // Set the data
      setAllTripResults(trips);
      setVessels(vesselsWithTrips);
      setPorts(portsWithTrips.map(p => ({ name: p.name || p, state: p.state || '' })));
      setAllAssetValuations(assetData);
      setAllQuotaTransactions(quotaData);
      setQuotaTradingActivity(activityData);
      setFleetQuotaUtilization(fleetQuotaData);

      // Extract years
      const years = [];
      if (trips.length > 0) {
        const yearSet = new Set();
        trips.forEach((trip) => {
          if (trip.simulation_year !== null && trip.simulation_year !== undefined) {
            yearSet.add(trip.simulation_year);
          }
          if (trip.year !== null && trip.year !== undefined) {
            yearSet.add(trip.year);
          }
        });
        years.push(...Array.from(yearSet).sort((a, b) => b - a));
      }

      setAvailableYears(years);
      console.log('Trip Economist data loaded successfully:', { 
        sessionId: selectedSessionId,
        tripCount: trips.length,
        assetValuations: assetData.length,
        quotaTransactions: quotaData.length,
        tradingActivity: activityData.length,
        fleetQuotaUtilization: fleetQuotaData ? 'loaded' : 'not loaded'
      });

    } catch (err) {
      console.error('Error loading trip economist data:', err);
      setError(`Failed to load trip economist data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [selectedScope, selectedVessel, selectedPort, selectedYear, selectedSessionId, getTripResults, getFishingVessels, getFishingPorts, getAssetValuations, getQuotaMarketTransactions, getQuotaTradingActivity, getFleetQuotaUtilization]);

  // LOAD DATA when scope/selection changes OR when session is selected
  useEffect(() => {
    if (isModalOpen && selectedSessionId) {
      loadData();
    }
  }, [selectedScope, selectedVessel, selectedPort, selectedSessionId, isModalOpen, loadData]);

  // ANALYSIS FUNCTIONS
  const getQuotaAnalysis = useCallback((data) => {
    if (!data.length) return { analysis: [], chokeSpecies: null };

    const quotaTypes = [
      { name: 'GB Cod', remaining: 'remaining_gb_cod_quota_lbs', price: 3.50 },
      { name: 'GB Cod East', remaining: 'remaining_gb_cod_east_quota_lbs', price: 3.50 },
      { name: 'GOM Cod', remaining: 'remaining_gom_cod_quota_lbs', price: 3.50 },
      { name: 'GB Haddock', remaining: 'remaining_gb_haddock_quota_lbs', price: 2.80 },
      { name: 'GB Haddock East', remaining: 'remaining_gb_haddock_east_quota_lbs', price: 2.80 },
      { name: 'GOM Haddock', remaining: 'remaining_gom_haddock_quota_lbs', price: 2.80 },
      { name: 'GB Yellowtail Flounder', remaining: 'remaining_gb_yellowtail_flounder_quota_lbs', price: 2.20 },
      { name: 'SNE/MA Yellowtail Flounder', remaining: 'remaining_sne_ma_yellowtail_flounder_quota_lbs', price: 2.20 },
      { name: 'CC/GOM Yellowtail Flounder', remaining: 'remaining_cc_gom_yellowtail_flounder_quota_lbs', price: 2.20 },
      { name: 'Plaice', remaining: 'remaining_plaice_quota_lbs', price: 1.80 },
      { name: 'Witch Flounder', remaining: 'remaining_witch_flounder_quota_lbs', price: 2.10 },
      { name: 'GB Winter Flounder', remaining: 'remaining_gb_winter_flounder_quota_lbs', price: 3.20 },
      { name: 'GOM Winter Flounder', remaining: 'remaining_gom_winter_flounder_quota_lbs', price: 3.20 },
      { name: 'SNE Winter Flounder', remaining: 'remaining_sne_winter_flounder_quota_lbs', price: 3.20 },
      { name: 'Redfish', remaining: 'remaining_redfish_quota_lbs', price: 1.50 },
      { name: 'White Hake', remaining: 'remaining_white_hake_quota_lbs', price: 1.40 },
      { name: 'Pollock', remaining: 'remaining_pollock_quota_lbs', price: 1.20 },
      { name: 'Northern Windowpane', remaining: 'remaining_northern_windowpane_quota_lbs', price: 0.80 },
      { name: 'Southern Windowpane', remaining: 'remaining_southern_windowpane_quota_lbs', price: 0.80 },
      { name: 'Ocean Pout', remaining: 'remaining_ocean_pout_quota_lbs', price: 1.10 },
      { name: 'Halibut', remaining: 'remaining_halibut_quota_lbs', price: 4.20 },
      { name: 'Wolffish', remaining: 'remaining_wolffish_quota_lbs', price: 1.60 }
    ];

    const analysis = quotaTypes.map(type => {
      const vesselData = vessels.find(v => v.name === data[0]?.vessel_name);
      if (!vesselData) return null;

      const quotaField = type.remaining.replace('remaining_', '');
      const totalQuota = vesselData[quotaField] || 0;
      
      const latestTrip = data[data.length - 1];
      const totalRemaining = latestTrip[type.remaining] || totalQuota;
      
      const used = totalQuota - totalRemaining;
      const utilization = totalQuota > 0 ? (used / totalQuota) * 100 : 0;
      const missedRevenue = totalRemaining * type.price;

      return {
        name: type.name,
        quota: totalQuota,
        used,
        remaining: totalRemaining,
        utilization: Math.round(utilization * 100) / 100,
        missedRevenue: Math.round(missedRevenue)
      };
    }).filter(Boolean);

    const chokeSpecies = analysis.length > 0 ? analysis.reduce((max, current) => 
      current.utilization > max.utilization ? current : max
    ) : null;

    return { analysis, chokeSpecies };
  }, [vessels]);

  const getVesselStats = useCallback(() => {
    if (selectedScope !== 'vessel' || !selectedVessel) return null;

    const vesselData = vessels.find(v => v.name === selectedVessel);
    if (!vesselData) return null;

    return [
      { metric: 'Crew Size', value: vesselData.crew_size || 0 },
      { metric: 'Fuel Efficiency (L/hr)', value: `${(vesselData.fuel_efficiency_l_per_hr || 0).toFixed(1)} L/hr` },
      { metric: 'Catch Time (hrs)', value: `${(vesselData.catch_time_hrs || 0).toFixed(1)} hrs` },
      { metric: 'Outbound Speed (kts)', value: `${(vesselData.outbound_speed_kts || 0).toFixed(1)} kts` },
      { metric: 'Return Speed (kts)', value: `${(vesselData.return_speed_kts || 0).toFixed(1)} kts` },
      { metric: 'Max Capacity (lbs)', value: new Intl.NumberFormat('en-US').format(vesselData.max_capacity_lbs || 0) }
    ];
  }, [selectedScope, selectedVessel, vessels]);

  const getExpenseAnalysis = useCallback((data) => {
    if (!data.length) return [];

    const totals = data.reduce((acc, trip) => {
      const fuelCosts = (trip.outbound_fuel_cost || 0) + (trip.fish_fuel_cost || 0) + (trip.return_fuel_cost || 0);
      const laborCosts = (trip.total_labor_cost || 0) + (trip.captain_wage || 0) + (trip.crew_total_wage || 0);
      
      return {
        fuel: acc.fuel + fuelCosts,
        labor: acc.labor + laborCosts,
        food: acc.food + (trip.food_cost || 0),
        ice: acc.ice + (trip.ice_cost || 0),
        water: acc.water + (trip.water_cost || 0)
      };
    }, { fuel: 0, labor: 0, food: 0, ice: 0, water: 0 });

    const totalExpenses = Object.values(totals).reduce((sum, val) => sum + val, 0);
    
    if (totalExpenses === 0) return [];

    return [
      { name: 'Fuel', value: Math.round(totals.fuel), percentage: Math.round((totals.fuel / totalExpenses) * 100) },
      { name: 'Labor', value: Math.round(totals.labor), percentage: Math.round((totals.labor / totalExpenses) * 100) },
      { name: 'Food', value: Math.round(totals.food), percentage: Math.round((totals.food / totalExpenses) * 100) },
      { name: 'Ice', value: Math.round(totals.ice), percentage: Math.round((totals.ice / totalExpenses) * 100) },
      { name: 'Water', value: Math.round(totals.water), percentage: Math.round((totals.water / totalExpenses) * 100) }
    ];
  }, []);

  const getPortComparison = useCallback((data) => {
    if (!data.length) return [];

    const portStats = {};
    data.forEach(trip => {
      if (!portStats[trip.port_name]) {
        portStats[trip.port_name] = { value: 0, volume: 0 };
      }
      portStats[trip.port_name].value += trip.total_revenue || 0;
      portStats[trip.port_name].volume += trip.total_landings_lbs || 0;
    });

    return Object.entries(portStats)
      .map(([port, stats]) => ({
        port,
        value: Math.round(stats.value),
        volume: Math.round(stats.volume)
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, []);

  // MEMOIZED ANALYSIS (updates when filtered data changes)
  const quotaAnalysis = useMemo(() => getQuotaAnalysis(filteredTripData), [getQuotaAnalysis, filteredTripData]);
  const vesselStats = useMemo(() => getVesselStats(), [getVesselStats]);
  const expenseAnalysis = useMemo(() => getExpenseAnalysis(filteredTripData), [getExpenseAnalysis, filteredTripData]);
  const portComparison = useMemo(() => 
    selectedScope !== 'vessel' && selectedScope !== 'top5' ? getPortComparison(filteredTripData) : null, 
    [selectedScope, getPortComparison, filteredTripData]
  );

  const formatCurrency = useCallback((value) => {
    if (!value && value !== 0) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  }, []);

  return (
    <>
      <button 
        className="choke-analysis-button"
        onClick={() => setIsModalOpen(true)}
        type="button"
        style={{ marginTop: '0px' }}
      >
        View Choke Stock Analysis
      </button>
        
      {isModalOpen && createPortal(
        <Modal
          title="Trip Economist Details (BETA)"
          onClose={() => setIsModalOpen(false)}
          size="extra-large"
          customMaxWidth="95vw"
          customMaxHeight="95vh"
          bodyStyle={{ padding: '20px', overflowY: 'auto' }}
        >
          <div>
            {/* Session Loading State */}
            {sessionLoading ? (
              <div className="dashboard-loading">
                <div className="loading-spinner"></div>
                <p>Loading sessions...</p>
              </div>
            ) : (
              <>
                {/* Session Selection Controls */}
                <div className="dashboard-filters">
                  <div className="filter-group">
                    <label>Simulation Session:</label>
                    <select 
                      value={selectedSessionId} 
                      onChange={(e) => setSelectedSessionId(e.target.value)}
                    >
                      <option value="">Choose session...</option>
                      {sessions.map(session => (
                        <option key={session.session_id} value={session.session_id}>
                          {session.session_name || session.session_id} 
                          {session.start_year && session.end_year && ` (${session.start_year}-${session.end_year})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label>View Scope:</label>
                    <select 
                      value={selectedScope} 
                      onChange={(e) => handleScopeChange(e.target.value)}
                      disabled={!selectedSessionId}
                    >
                      <option value="vessel">Individual Vessel</option>
                      <option value="port">Port Vessels</option>
                      <option value="fleet">Entire Fleet</option>
                      <option value="top5">Top 5 Vessels by Revenue</option>
                    </select>
                  </div>

                  {selectedScope === 'vessel' && (
                    <div className="filter-group">
                      <label>Select Vessel:</label>
                      <select 
                        value={selectedVessel} 
                        onChange={(e) => setSelectedVessel(e.target.value)}
                        disabled={!selectedSessionId}
                      >
                        <option value="">Choose vessel...</option>
                        {vessels.map(vessel => (
                          <option key={vessel.name} value={vessel.name}>{vessel.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {selectedScope === 'port' && (
                    <div className="filter-group">
                      <label>Select Port:</label>
                      <select 
                        value={selectedPort} 
                        onChange={(e) => setSelectedPort(e.target.value)}
                        disabled={!selectedSessionId}
                      >
                        <option value="">Choose port...</option>
                        {ports.map(port => (
                          <option key={port.name || port} value={port.name || port}>
                            {port.name || port}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="filter-group">
                    <label>Year:</label>
                    <select 
                      value={selectedYear} 
                      onChange={(e) => setSelectedYear(e.target.value)}
                      disabled={!selectedSessionId}
                    >
                      <option value="total">All Years</option>
                      {availableYears.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {!selectedSessionId ? (
                  <div className="no-data-section">
                    <div className="no-data-message">
                      <h3>No Session Selected</h3>
                      <p>Please select a simulation session to view trip economics data.</p>
                      {sessions.length === 0 && (
                        <p>No simulation sessions found. Please run a simulation first to generate data.</p>
                      )}
                    </div>
                  </div>
                ) : loading ? (
                  <div className="dashboard-loading">
                    <div className="loading-spinner"></div>
                    <p>Loading trip economist data...</p>
                  </div>
                ) : error ? (
                  <div className="dashboard-error">
                    <p>{error}</p>
                    <button onClick={loadData} className="btn-primary">Retry</button>
                  </div>
                ) : filteredTripData.length > 0 ? (
                  <>
                    {/* 1. CHOKE ANALYSIS HEATMAP - TOP SECTION */}
                    {selectedSessionId && (
                      <ChokeAnalysis selectedSessionId={selectedSessionId} />
                    )}

                    {/* 2. MIDDLE SECTION - Expense Breakdown + Port Comparison Side by Side */}
                    <div className="insight-row">
                      {/* Expense Breakdown Component */}
                      <ExpenseBreakdownComponent 
                        expenseAnalysis={expenseAnalysis}
                        formatCurrency={formatCurrency}
                        EXPENSE_COLORS={EXPENSE_COLORS}
                      />

                      {/* Port Comparison Component */}
                      <PortComparisonComponent 
                        portComparison={portComparison}
                        formatCurrency={formatCurrency}
                      />
                    </div>

                    {/* 3. BOTTOM SECTION - Quota Utilization with ACL Scenario Support */}
                    {/* ACL Scenario Component for fleet scope */}
                    {selectedScope === 'fleet' && (
                      <div className="insight-container full-width">
                        <AclScenarioComponent
                          aclAnalyses={aclAnalyses}
                          selectedYear={selectedYear}
                          selectedSessionId={selectedSessionId}
                          loading={aclDataLoading}
                        />
                      </div>
                    )}

                    {/* Single Fleet Quota Utilization for fleet scope when no ACL scenarios */}
                    {selectedScope === 'fleet' && (!aclAnalyses || aclAnalyses.length === 0) && (
                      <SingleFleetQuotaUtilizationComponent 
                        fleetQuotaUtilization={fleetQuotaUtilization}
                        getQuotaUtilizationColor={getQuotaUtilizationColor}
                      />
                    )}

                    {/* Vessel Quota Utilization for vessel scope */}
                    {selectedScope === 'vessel' && quotaAnalysis.analysis.length > 0 && (
                      <VesselQuotaUtilizationComponent 
                        quotaAnalysis={quotaAnalysis}
                        selectedVessel={selectedVessel}
                        formatCurrency={formatCurrency}
                      />
                    )}

                    {/* COLLAPSIBLE DETAILED SECTIONS */}
                    
                    {/* FLEET-WIDE QUOTA UTILIZATION SECTION */}
                    {selectedScope === 'fleet' && fleetQuotaUtilization && fleetQuotaUtilization.success && (
                      <CollapsibleSection title="Detailed Fleet Quota Analysis">
                        {/* Detailed Fleet Quota Table */}
                        <div className="chart-container full-width">
                          <h3>Detailed Fleet Quota Metrics by Species</h3>
                          <div className="table-container">
                            <table className="results-table">
                              <thead>
                                <tr>
                                  <th>Species</th>
                                  <th>Fleet Quota (lbs)</th>
                                  <th>Fleet Catch (lbs)</th>
                                  <th>Utilization %</th>
                                  <th>Fleet Remaining (lbs)</th>
                                  <th>Avg Price/lb</th>
                                  <th>Missed Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {fleetQuotaUtilization.species_breakdown.map((species, index) => (
                                  <tr key={species.species_key}>
                                    <td style={{ fontWeight: 'bold' }}>{species.species_name}</td>
                                    <td>{species.fleet_quota_lbs.toLocaleString()}</td>
                                    <td>{species.fleet_catch_lbs.toLocaleString()}</td>
                                    <td style={{ 
                                      fontWeight: 'bold', 
                                      color: getQuotaUtilizationColor(species.utilization_pct)
                                    }}>
                                      {species.utilization_pct.toFixed(1)}%
                                    </td>
                                    <td>{species.fleet_remaining_lbs.toLocaleString()}</td>
                                    <td>{formatCurrency(species.avg_price_per_lb)}</td>
                                    <td style={{ fontWeight: 'bold', color: '#f59e0b' }}>
                                      {formatCurrency(species.missed_revenue)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* TOP 5 VESSELS ANALYSIS */}
                    {selectedScope === 'top5' && top5VesselsAnalysis && top5VesselsAnalysis.length > 0 && (
                      <CollapsibleSection title="Top 5 Vessels Analysis">
                        {/* Revenue Comparison Chart */}
                        <div className="chart-container full-width">
                          <h3>Top 5 Vessels by Total Revenue (All Trips Combined)</h3>
                          <ResponsiveContainer width="100%" height={400}>
                            <ComposedChart data={top5VesselsAnalysis} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis 
                                dataKey="vesselName" 
                                angle={-45}
                                textAnchor="end"
                                height={80}
                                interval={0}
                              />
                              <YAxis yAxisId="left" label={{ value: 'Revenue ($)', angle: -90, position: 'insideLeft' }} />
                              <YAxis yAxisId="right" orientation="right" label={{ value: 'Trips', angle: 90, position: 'insideRight' }} />
                              <Tooltip 
                                formatter={(value, name) => {
                                  if (name === 'totalRevenue') return [formatCurrency(value), 'Total Revenue'];
                                  if (name === 'totalTrips') return [value, 'Total Trips'];
                                  return [value, name];
                                }}
                              />
                              <Legend />
                              <Bar yAxisId="left" dataKey="totalRevenue" fill="#3b82f6" name="Total Revenue" />
                              <Line yAxisId="right" type="monotone" dataKey="totalTrips" stroke="#ef4444" strokeWidth={3} name="Total Trips" />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Top 5 Vessels Statistics Table */}
                        <div className="chart-container full-width">
                          <h3>Top 5 Vessels - Detailed Performance Statistics</h3>
                          <div className="table-container">
                            <table className="results-table">
                              <thead>
                                <tr>
                                  <th>Rank</th>
                                  <th>Vessel Name</th>
                                  <th>Total Revenue</th>
                                  <th>Total Trips</th>
                                  <th>Avg Revenue/Trip</th>
                                  <th>Total Profit</th>
                                  <th>Profit Margin</th>
                                  <th>Total Landings (lbs)</th>
                                  <th>Revenue/Pound</th>
                                </tr>
                              </thead>
                              <tbody>
                                {top5VesselsAnalysis.map((vessel, index) => (
                                  <tr key={vessel.vesselName}>
                                    <td style={{ fontWeight: 'bold', fontSize: '1.1em', color: '#3b82f6' }}>
                                      #{index + 1}
                                    </td>
                                    <td style={{ fontWeight: 'bold' }}>{vessel.vesselName}</td>
                                    <td style={{ fontWeight: 'bold', color: '#059669' }}>
                                      {formatCurrency(vessel.totalRevenue)}
                                    </td>
                                    <td>{vessel.totalTrips}</td>
                                    <td>{formatCurrency(vessel.avgRevenuePerTrip)}</td>
                                    <td className={vessel.totalProfit >= 0 ? 'positive' : 'negative'}>
                                      {formatCurrency(vessel.totalProfit)}
                                    </td>
                                    <td className={vessel.profitMargin >= 0 ? 'positive' : 'negative'}>
                                      {vessel.profitMargin.toFixed(1)}%
                                    </td>
                                    <td>{vessel.totalLandings.toLocaleString()}</td>
                                    <td>{formatCurrency(vessel.revenuePerPound)}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* ASSET VALUATION SECTION */}
                    {finalFilteredAssetValuations.length > 0 && (
                      <CollapsibleSection title="Asset Valuation Data">
                        <div className="chart-container full-width">
                          <h3>
                            Asset Valuation Data
                            {selectedScope === 'vessel' && ` - ${selectedVessel}`}
                            {selectedScope === 'port' && ` - ${selectedPort} Port Vessels`}
                            {selectedScope === 'top5' && ` - Top 5 Vessels`}
                          </h3>
                          <div className="table-container">
                            <table className="results-table">
                              <thead>
                                <tr>
                                  <th>Vessel</th>
                                  <th>Valuation Date</th>
                                  <th>Year</th>
                                  <th>Method</th>
                                  <th>Total Asset Value</th>
                                  <th>Vessel Value</th>
                                  <th>Permit Value</th>
                                  <th>NPV</th>
                                  <th>Confidence Level</th>
                                  <th>Market Conditions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {finalFilteredAssetValuations.slice(0, 50).map((valuation, index) => (
                                  <tr key={valuation.valuation_id || index}>
                                    <td style={{ fontWeight: 'bold' }}>{valuation.vessel_name}</td>
                                    <td>{formatDate(valuation.valuation_date)}</td>
                                    <td>{valuation.simulation_year}</td>
                                    <td style={{ textTransform: 'uppercase' }}>{valuation.valuation_method}</td>
                                    <td style={{ fontWeight: 'bold', color: '#059669' }}>
                                      {formatCurrency(valuation.total_asset_value)}
                                    </td>
                                    <td>{formatCurrency(valuation.vessel_market_value)}</td>
                                    <td>{formatCurrency(valuation.total_permit_value)}</td>
                                    <td className={valuation.net_present_value >= 0 ? 'positive' : 'negative'}>
                                      {formatCurrency(valuation.net_present_value)}
                                    </td>
                                    <td>{((valuation.valuation_confidence_level || 0) * 100).toFixed(1)}%</td>
                                    <td>{(valuation.market_conditions_rating || 0).toFixed(1)}/10</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* QUOTA MARKET TRANSACTIONS SECTION */}
                    {finalFilteredQuotaTransactions.length > 0 && (
                      <CollapsibleSection title="Quota Market Transactions">
                        <div className="chart-container full-width">
                          <h3>
                            Quota Market Transactions
                            {selectedScope === 'vessel' && ` - ${selectedVessel}`}
                            {selectedScope === 'port' && ` - ${selectedPort} Port Vessels`}
                            {selectedScope === 'top5' && ` - Top 5 Vessels`}
                          </h3>
                          <div className="table-container">
                            <table className="results-table">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th>Year</th>
                                  <th>Type</th>
                                  <th>Buyer</th>
                                  <th>Seller</th>
                                  <th>Species</th>
                                  <th>Quota Amount (lbs)</th>
                                  <th>Price/lb</th>
                                  <th>Total Value</th>
                                  <th>Market Conditions</th>
                                  <th>Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {finalFilteredQuotaTransactions
                                  .sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date))
                                  .slice(0, 50)
                                  .map((transaction, index) => (
                                    <tr key={transaction.transaction_id || index}>
                                      <td>{formatDate(transaction.transaction_date)}</td>
                                      <td>{transaction.simulation_year}</td>
                                      <td style={{ 
                                        textTransform: 'uppercase', 
                                        fontWeight: 'bold',
                                        color: transaction.transaction_type === 'buy' ? '#059669' : 
                                               transaction.transaction_type === 'sell' ? '#dc2626' : '#3b82f6'
                                      }}>
                                        {transaction.transaction_type}
                                      </td>
                                      <td>{transaction.buyer_vessel || 'N/A'}</td>
                                      <td>{transaction.seller_vessel || 'N/A'}</td>
                                      <td style={{ fontWeight: 'bold' }}>{transaction.species}</td>
                                      <td>{(transaction.quota_amount_lbs || 0).toLocaleString()}</td>
                                      <td>{formatCurrency(transaction.price_per_lb)}</td>
                                      <td style={{ fontWeight: 'bold', color: '#059669' }}>
                                        {formatCurrency(transaction.total_transaction_value)}
                                      </td>
                                      <td>{(transaction.market_conditions_rating || 0).toFixed(1)}/10</td>
                                      <td style={{ 
                                        textTransform: 'capitalize',
                                        color: transaction.transaction_status === 'completed' ? '#059669' : '#f59e0b'
                                      }}>
                                        {transaction.transaction_status}
                                      </td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* QUOTA TRADING ACTIVITY SECTION - Only show for fleet scope */}
                    {selectedScope === 'fleet' && quotaTradingActivity.length > 0 && (
                      <CollapsibleSection title="Fleet-Wide Quota Trading Activity">
                        <div className="chart-container full-width">
                          <h3>Fleet-Wide Quota Trading Activity Summary</h3>
                          <div className="table-container">
                            <table className="results-table">
                              <thead>
                                <tr>
                                  <th>Year</th>
                                  <th>Species</th>
                                  <th>Transaction Type</th>
                                  <th>Transaction Count</th>
                                  <th>Total Quota Traded (lbs)</th>
                                  <th>Avg Price/lb</th>
                                  <th>Total Market Value</th>
                                  <th>Avg Market Conditions</th>
                                </tr>
                              </thead>
                              <tbody>
                                {quotaTradingActivity
                                  .sort((a, b) => (b.simulation_year || 0) - (a.simulation_year || 0))
                                  .map((activity, index) => (
                                    <tr key={index}>
                                      <td>{activity.simulation_year}</td>
                                      <td style={{ fontWeight: 'bold' }}>{activity.species}</td>
                                      <td style={{ 
                                        textTransform: 'uppercase',
                                        color: activity.transaction_type === 'buy' ? '#059669' : 
                                               activity.transaction_type === 'sell' ? '#dc2626' : '#3b82f6'
                                      }}>
                                        {activity.transaction_type}
                                      </td>
                                      <td>{activity.transaction_count}</td>
                                      <td>{(activity.total_quota_traded || 0).toLocaleString()}</td>
                                      <td>{formatCurrency(activity.avg_price_per_lb)}</td>
                                      <td style={{ fontWeight: 'bold', color: '#059669' }}>
                                        {formatCurrency(activity.total_market_value)}
                                      </td>
                                      <td>{(activity.avg_market_conditions || 0).toFixed(1)}/10</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Vessel Statistics - only show for individual vessel */}
                    {selectedScope === 'vessel' && vesselStats && (
                      <CollapsibleSection title="Vessel Statistics">
                        <div className="chart-container">
                          <h3>Vessel Statistics - {selectedVessel}</h3>
                          <div className="table-container">
                            <table className="results-table">
                              <thead>
                                <tr>
                                  <th>Metric</th>
                                  <th>Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {vesselStats.map((stat, index) => (
                                  <tr key={index}>
                                    <td>{stat.metric}</td>
                                    <td>{stat.value}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </CollapsibleSection>
                    )}

                    {/* Trip Results Data Table */}
                    <CollapsibleSection title="Trip Results Data">
                      <div className="recent-results">
                        <h3>
                          {selectedScope === 'top5' 
                            ? 'Trip Results Data - Top 5 Vessels' 
                            : 'Trip Results Data'
                          }
                        </h3>
                        <div className="table-container">
                          <table className="results-table">
                            <thead>
                              <tr>
                                <th>Trip ID</th>
                                <th>Vessel</th>
                                <th>Port</th>
                                <th>Year</th>
                                <th>Revenue</th>
                                <th>Total Costs</th>
                                <th>Profit</th>
                                <th>Margin</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(selectedScope === 'top5' && top5VesselsAnalysis 
                                ? filteredTripData.filter(trip => 
                                    top5VesselsAnalysis.some(vessel => vessel.vesselName === trip.vessel_name)
                                  )
                                : filteredTripData
                              ).slice(0, 50).map((trip, index) => {
                                const profit = trip.final_net_profit || 0;
                                const margin = trip.total_revenue > 0 ? (profit / trip.total_revenue * 100) : 0;
                                
                                return (
                                  <tr key={trip.trip_id || index}>
                                    <td>{trip.trip_id}</td>
                                    <td>{trip.vessel_name}</td>
                                    <td>{trip.port_name}</td>
                                    <td>{trip.simulation_year || trip.year}</td>
                                    <td>{formatCurrency(trip.total_revenue)}</td>
                                    <td>{formatCurrency(trip.total_costs)}</td>
                                    <td className={profit >= 0 ? 'positive' : 'negative'}>
                                      {formatCurrency(profit)}
                                    </td>
                                    <td className={margin >= 0 ? 'positive' : 'negative'}>
                                      {margin.toFixed(1)}%
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        <div style={{ padding: '16px 24px', fontSize: '14px', color: '#6b7280', borderTop: '1px solid #f3f4f6' }}>
                          Showing {Math.min(50, selectedScope === 'top5' && top5VesselsAnalysis 
                            ? filteredTripData.filter(trip => 
                                top5VesselsAnalysis.some(vessel => vessel.vesselName === trip.vessel_name)
                              ).length
                            : filteredTripData.length
                          )} of {selectedScope === 'top5' && top5VesselsAnalysis 
                            ? filteredTripData.filter(trip => 
                                top5VesselsAnalysis.some(vessel => vessel.vesselName === trip.vessel_name)
                              ).length
                            : filteredTripData.length
                          } trips
                        </div>
                      </div>
                    </CollapsibleSection>
                  </>
                ) : (
                  <div className="no-data-section">
                    <div className="no-data-message">
                      <h3>No data available</h3>
                      <p>Please select a vessel, port, or check if data exists for the selected criteria.</p>
                      {selectedScope === 'vessel' && !selectedVessel && (
                        <p>Please select a vessel to view individual vessel data.</p>
                      )}
                      {selectedScope === 'port' && !selectedPort && (
                        <p>Please select a port to view port-specific data.</p>
                      )}
                      {selectedScope === 'top5' && (
                        <p>No vessel data available to generate top 5 revenue analysis.</p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </Modal>,
        document.body
      )}
    </>
  );
};

export default TripEconomistDetails;